import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';

const { 
  FiDownload, 
  FiMessageCircle, 
  FiCalendar, 
  FiX, 
  FiChevronRight
} = FiIcons;

const App = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const steps = [
    {
      id: 'STEP 1',
      title: 'Download Your Power BI E-Book',
      description: 'Get instant access to your comprehensive guide and start mastering data visualization today.',
      buttonText: 'Download E-Book Now',
      buttonColor: 'bg-[#22c55e]',
      hoverColor: 'hover:bg-[#16a34a]',
      icon: FiDownload,
      action: () => window.open('https://media-manager-c.questera.ai/greta-media/d1e1f795f547d188310881b3e1dfad84afccd53626664f205958a3becb1b00f4f5d96fb34b3d15472c5f30d572e92287/documents/YXBwbGljYXRpb24vcGRm/6e916033d4711aa40dd4cba861fec29a.pdf', '_blank')
    },
    {
      id: 'STEP 2',
      title: 'Join the WhatsApp Community',
      description: 'Connect with 5,000+ data professionals, get daily tips, and stay updated with the latest AI trends.',
      buttonText: 'Join Community',
      buttonColor: 'bg-[#3b82f6]',
      hoverColor: 'hover:bg-[#2563eb]',
      icon: FiMessageCircle,
      action: () => window.open('https://chat.whatsapp.com/GWMhCNCoUGhFuBkqFUUMhL', '_blank')
    },
    {
      id: 'STEP 3',
      title: 'Book Your One-to-One Coaching',
      description: 'Accelerate your career with a personalized roadmap from industry experts. Limited slots available.',
      buttonText: 'Reserve Your Slot',
      buttonColor: 'bg-[#f97316]',
      hoverColor: 'hover:bg-[#ea580c]',
      icon: FiCalendar,
      action: () => setIsModalOpen(true)
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#0b0f1a]">
      {/* Updated White Header */}
      <header className="bg-white w-full border-b border-gray-200 py-3 px-4 md:px-8 shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src="https://media-manager-c.questera.ai/greta-media/d1e1f795f547d188310881b3e1dfad84afccd53626664f205958a3becb1b00f4f5d96fb34b3d15472c5f30d572e92287/images/aW1hZ2UvcG5n/8bb40739d18b978a054bac271b6f4417.png" 
              alt="Yinguru Academy Logo" 
              className="h-10 md:h-12 w-auto object-contain"
            />
          </div>

          <div className="text-center flex-1">
            <h1 className="text-lg md:text-2xl font-black text-gray-900 tracking-tighter">
              Yinguru Academy
            </h1>
            <p className="text-[9px] md:text-xs font-bold text-red-600 uppercase tracking-[0.2em]">
              UNLEASH THE POWER OF UNSEEN
            </p>
          </div>

          <div>
            <button 
              onClick={() => setIsModalOpen(true)}
              className="bg-[#facc15] border-2 border-[#facc15] text-black text-[10px] md:text-sm font-extrabold px-3 md:px-6 py-2 rounded-full hover:bg-transparent hover:text-black transition-all duration-300 transform hover:scale-105 shadow-md"
            >
              Book Your One-to-One Coaching
            </button>
          </div>
        </div>
      </header>

      <main className="flex-grow">
        <section className="pt-20 pb-12 px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-6xl font-black mb-6 tracking-tight text-white">
              🎉 Your Power BI <span className="text-[#facc15]">E-Book Is Ready</span>
            </h2>
            <p className="text-xl md:text-2xl text-white/70 font-medium max-w-2xl mx-auto">
              Follow the steps below to get maximum value.
            </p>
          </motion.div>
        </section>

        <section className="max-w-3xl mx-auto px-4 pb-24">
          <div className="space-y-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.2, duration: 0.5 }}
                className="dark-gradient-card rounded-3xl p-8 relative overflow-hidden group"
              >
                <div className="absolute top-0 left-0">
                  <div className="step-pill px-6 py-2 rounded-br-2xl uppercase">
                    {step.id}
                  </div>
                </div>

                <div className="mt-6 flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3 flex items-center gap-3 text-white">
                      <SafeIcon icon={step.icon} className="text-[#facc15]" />
                      {step.title}
                    </h3>
                    <p className="text-white/70 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                  
                  <div className="flex-shrink-0">
                    <button
                      onClick={step.action}
                      className={`${step.buttonColor} ${step.hoverColor} text-white font-bold px-8 py-4 rounded-2xl flex items-center justify-center gap-2 transition-all duration-300 transform hover:scale-105 shadow-xl group-hover:shadow-2xl`}
                    >
                      <span>{step.buttonText}</span>
                      <FiChevronRight className="group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>

                <div className="absolute -right-4 -bottom-4 opacity-5 group-hover:opacity-10 transition-opacity">
                  <SafeIcon icon={step.icon} className="text-9xl text-white" />
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      </main>

      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-overlay"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-[#11162a] w-full max-w-4xl max-h-[90vh] rounded-3xl overflow-hidden shadow-2xl relative border border-white/10"
            >
              <div className="p-6 border-b border-white/10 flex items-center justify-between bg-[#11162a]">
                <h3 className="text-xl font-bold flex items-center gap-2 text-white">
                  <FiCalendar className="text-[#f97316]" />
                  Book Your One-to-One Coaching
                </h3>
                <button 
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors text-white"
                >
                  <FiX size={24} />
                </button>
              </div>

              <div className="h-[70vh] w-full bg-white">
                <iframe
                  src="https://dmc.yinguru.com/widget/form/XzuWldyFp4gKNmyytxME"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title="Booking Form"
                ></iframe>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <footer className="bg-black py-12 px-4 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
             <div className="w-8 h-8 bg-white/10 rounded flex items-center justify-center">
              <span className="text-white font-black text-sm italic">Y</span>
            </div>
            <p className="text-white/50 text-sm font-medium">
              © 2026 Power BI Career Mastery Program. All Rights Reserved.
            </p>
          </div>
          
          <div className="flex gap-8">
            <a href="#" className="text-white/50 hover:text-white text-xs uppercase tracking-widest transition-colors font-bold">Privacy Policy</a>
            <a href="#" className="text-white/50 hover:text-white text-xs uppercase tracking-widest transition-colors font-bold">Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;